import { createSlice } from "@reduxjs/toolkit";
import {
  getStoreManagementStats,
  getStoreStockValue,
  getStoreActivityLogs,
  addStoreManager,
  addStore,
  getAllStores,
  getAllStoreManagers,
  getAllStoreLocations,
  getAllStockRequests,
  getStoreAnalytics,
  getRestockRequests,
  getStockTransfers,
  getLowStockItems,
  getStockRequestById,
  createRequestNote,
  getStockRecord,
  getStockNotes,
  getAllStoresStats,
  getStockRequestTimeline,
  approveStockRequest,
  dispatchStockRequest,
  confirmStockRequest,
  getStoreManagerOrders,
} from "./storeManagementActions";
import storage from "redux-persist/lib/storage";
import { persistReducer } from "redux-persist";

const initialState = {
  // Stats data - Mock data for demo
  totalStores: 0,
  totalStoreManagers: 0,
  stockRequests: 0,
  stockRequestsUrgent: 5,

  // Stock value data - API data
  totalStockValue: 0,
  totalSales: 0,
  stockValueByLocation: [],
  salesByLocation: [],
  filterPeriod: "1month",

  // Stores data
  storesData: [],
  storesStats: {},
  storesTotalCount: 0,
  storesLoading: false,
  storesError: null,
  allStoreLocationsLoading: false,
  allStoreLocations: [],

  // Store Managers data
  storeManagersData: [],
  storeManagersTotalCount: 0,
  storeManagersCurrentPage: 1,
  storeManagersTotalPages: 1,
  storeManagersLoading: false,
  storeManagersError: null,

  // Stock Requests data
  stockRequestsData: [],
  stockRequestsTotalCount: 0,
  stockRequestsCurrentPage: 1,
  stockRequestsTotalPages: 1,
  stockRequestsLoading: false,
  stockRequestsError: null,
  stockRequestsTabCounts: {
    PENDING: 0,
    APPROVED: 0,
    Dispatched: 0,
    Confirmed: 0,
    Rejected: 0,
  },

  stockRequestTimeline: [],
  stockRequestTimelineLoading: false,
  stockRequestTimelineError: null,

  // Single Stock Request data
  currentStockRequest: null,
  currentStockRequestLoading: false,
  currentStockRequestError: null,

  // Store Analytics data
  storeAnalyticsData: [],
  storeAnalyticsTotalCount: 0,
  storeAnalyticsCurrentPage: 1,
  storeAnalyticsTotalPages: 1,
  storeAnalyticsLoading: false,
  storeAnalyticsError: null,

  // Restock Requests data
  restockRequestsData: [],
  restockRequestsTotalCount: 0,
  restockRequestsCurrentPage: 1,
  restockRequestsTotalPages: 1,
  restockRequestsLoading: false,
  restockRequestsError: null,

  // Stock Transfers data
  stockTransfersData: [],
  stockTransfersTotalCount: 0,
  stockTransfersCurrentPage: 1,
  stockTransfersTotalPages: 1,
  stockTransfersLoading: false,
  stockTransfersError: null,

  // Low Stock data
  lowStockData: [],
  lowStockTotalCount: 0,
  lowStockCurrentPage: 1,
  lowStockTotalPages: 1,
  lowStockLoading: false,
  lowStockError: null,

  // Get Stock Record
  stockRecordData: [],
  stockRecordTotalCount: 0,
  stockRecordCurrentPage: 1,
  stockRecordTotalPages: 1,
  stockRecordTotalQuantity: 0,
  stockRecordLoading: false,
  stockRecordError: null,

  // Store Managers Order data
  storeManagerOrders: [],
  storeManagerOrdersTotalCount: 0,
  storeManagerOrdersCurrentPage: 1,
  storeManagerOrdersTotalPages: 1,
  storeManagerOrdersLoading: false,
  storeManagerOrdersError: null,
  storeManagerOrdersPageSize: 10,

  //Get Notes
  stockNotes: [],
  stockNotesLoading: false,
  stockNotesError: null,

  // Activity logs - Mock data for demo
  activityLogs: [],
  activityLogsTotalCount: 0,

  // Loading states
  loading: false,
  statsLoading: false,
  stockValueLoading: false,
  activityLogsLoading: false,

  // Action loading states
  addStoreManagerLoading: false,
  addStoreLoading: false,
  addStockLoading: false,
  createRequestNoteLoading: false,

  confirmStockLoading: false,
  confirmStockError: null,

  // Error states
  error: null,
  statsError: null,
  stockValueError: null,
  activityLogsError: null,
  addStoreManagerError: null,
  addStoreError: null,
  addStockError: null,
};

const storeManagementPersistConfig = {
  key: "storeManagement",
  storage,
};

export const storeManagementSlice = createSlice({
  name: "storeManagement",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.statsError = null;
      state.stockValueError = null;
      state.activityLogsError = null;
      state.addStoreManagerError = null;
      state.addStoreError = null;
      state.addStockError = null;
    },
  },
  extraReducers: {
    // Get store management stats
    [getStoreManagementStats.pending]: (state) => {
      state.statsLoading = true;
      state.statsError = null;
    },
    [getStoreManagementStats.fulfilled]: (state, { payload }) => {
      state.statsLoading = false;
      console.log(payload);
      state.totalStores = payload.data?.total_stores || 0;
      state.totalStoreManagers = payload.data?.total_store_managers || 0;
      state.stockRequests = payload.data?.stock_requests || 0;
      state.stockRequestsUrgent = payload.data?.stock_requests || 0;
    },
    [getStoreManagementStats.rejected]: (state, { payload }) => {
      state.statsLoading = false;
      state.statsError = payload?.message || "Failed to fetch store stats";
      state.totalStores = 0;
      state.totalStoreManagers = 0;
      state.storeRequests = 0;
      state.storeRequestsUrgent = 0;
    },

    // Get store stock value
    [getStoreStockValue.pending]: (state) => {
      state.stockValueLoading = true;
      state.stockValueError = null;
    },
    [getStoreStockValue.fulfilled]: (state, { payload }) => {
      state.stockValueLoading = false;

      // Extract data from the new API response structure
      const stockData = payload.data?.stock_data || {};
      const salesData = payload.data?.sales_data || {};

      // Set total values
      state.totalStockValue = parseFloat(stockData.total_stock_value || 0);
      state.totalSales = parseFloat(salesData.total_sales || 0);
      state.filterPeriod = payload.data?.filter_period || "1month";

      // Transform store_stock_values object to array for charts
      const stockValues = stockData.store_stock_values || {};
      state.stockValueByLocation = Object.entries(stockValues).map(
        ([storeId, value]) => ({
          store_id: storeId,
          name: storeId, // You might want to map this to actual store names
          stock_value: parseFloat(value || 0),
        })
      );

      // Transform store_sales object to array for charts
      const storeSales = salesData.store_sales || {};
      state.salesByLocation = Object.entries(storeSales).map(
        ([storeId, value]) => ({
          store_id: storeId,
          name: storeId, // You might want to map this to actual store names
          sales: parseFloat(value || 0),
        })
      );
    },
    [getStoreStockValue.rejected]: (state, { payload }) => {
      state.stockValueLoading = false;
      state.stockValueError =
        payload?.message || "Failed to fetch stock value data";
      state.totalStockValue = 0;
      state.totalSales = 0;
      state.stockValueByLocation = [];
      state.salesByLocation = [];
    },

    // Get store activity logs
    [getStoreActivityLogs.pending]: (state) => {
      state.activityLogsLoading = true;
      state.activityLogsError = null;
    },
    [getStoreActivityLogs.fulfilled]: (state, { payload }) => {
      state.activityLogsLoading = false;
      state.activityLogs = payload.data || [];
      state.activityLogsTotalCount = payload.total_items || 0;
    },
    [getStoreActivityLogs.rejected]: (state, { payload }) => {
      state.activityLogsLoading = false;
      state.activityLogsError =
        payload?.message || "Failed to fetch activity logs";
      state.activityLogs = [];
      state.activityLogsTotalCount = 0;
    },

    // Add store manager
    [addStoreManager.pending]: (state) => {
      state.addStoreManagerLoading = true;
      state.addStoreManagerError = null;
    },
    [addStoreManager.fulfilled]: (state, { payload }) => {
      state.addStoreManagerLoading = false;
    },
    [addStoreManager.rejected]: (state, { payload }) => {
      state.addStoreManagerLoading = false;
      state.addStoreManagerError =
        payload?.error || "Failed to add store manager";
    },

    // Add store
    [addStore.pending]: (state) => {
      state.addStoreLoading = true;
      state.addStoreError = null;
    },
    [addStore.fulfilled]: (state, { payload }) => {
      state.addStoreLoading = false;
    },
    [addStore.rejected]: (state, { payload }) => {
      state.addStoreLoading = false;
      state.addStoreError = payload?.message || "Failed to add store";
    },

    // Create a request note
    [createRequestNote.pending]: (state) => {
      state.createRequestNoteLoading = true;
      state.createRequestNoteError = null;
    },
    [createRequestNote.fulfilled]: (state, { payload }) => {
      state.createRequestNoteLoading = false;
    },
    [createRequestNote.rejected]: (state, { payload }) => {
      state.createRequestNoteLoading = false;
      state.createRequestNoteError =
        payload?.message || "Failed to create request note";
    },

    // Get all stores
    [getAllStores.pending]: (state) => {
      state.storesLoading = true;
      state.storesError = null;
    },
    [getAllStores.fulfilled]: (state, { payload }) => {
      state.storesLoading = false;
      state.storesData = payload.data || [];
      state.storesTotalCount = payload.total_items || 0;
    },
    [getAllStores.rejected]: (state, { payload }) => {
      state.storesLoading = false;
      state.storesError = payload?.message || "Failed to fetch stores";
      state.storesData = [];
      state.storesTotalCount = 0;
    },

    // get all store stats
    [getAllStoresStats.pending]: (state) => {
      state.storesLoading = true;
    },
    [getAllStoresStats.fulfilled]: (state, { payload }) => {
      state.storesLoading = false;
      state.storesStats = payload.data;
    },
    [getAllStoresStats.rejected]: (state, { payload }) => {
      state.storesLoading = false;
      state.storesError = payload?.message || "Failed to fetch stores";
    },

    // get all store locations
    [getAllStoreLocations.pending]: (state) => {
      state.allStoreLocationsLoading = true;
      state.storesError = null;
    },
    [getAllStoreLocations.fulfilled]: (state, { payload }) => {
      state.allStoreLocationsLoading = false;
      state.allStoreLocations = payload.data || [];
    },
    [getAllStoreLocations.rejected]: (state, { payload }) => {
      state.allStoreLocationsLoading = false;
      state.allStoreLocations = [];
    },

    // Get all store managers
    [getAllStoreManagers.pending]: (state) => {
      state.storeManagersLoading = true;
      state.storeManagersError = null;
    },
    [getAllStoreManagers.fulfilled]: (state, { payload }) => {
      state.storeManagersLoading = false;
      state.storeManagersData = payload.data || [];
      state.storeManagersTotalCount = payload.total_items || 0;
      state.storeManagersCurrentPage = payload.current_page || 1;
      state.storeManagersTotalPages = payload.total_pages || 1;
    },
    [getAllStoreManagers.rejected]: (state, { payload }) => {
      state.storeManagersLoading = false;
      state.storeManagersError =
        payload?.message || "Failed to fetch store managers";
      state.storeManagersData = [];
      state.storeManagersTotalCount = 0;
      state.storeManagersCurrentPage = 1;
      state.storeManagersTotalPages = 1;
    },

    // Get all stock requests
    [getAllStockRequests.pending]: (state) => {
      state.stockRequestsLoading = true;
      state.stockRequestsError = null;
    },
    [getAllStockRequests.fulfilled]: (state, { payload }) => {
      state.stockRequestsLoading = false;
      state.stockRequestsData = payload.data || [];
      state.stockRequestsTotalCount = payload.total_items || 0;
      state.stockRequestsCurrentPage = payload.current_page || 1;
      state.stockRequestsTotalPages = payload.total_pages || 1;
      // Store tab counts
      state.stockRequestsTabCounts = payload.tab_counts || {
        PENDING: 0,
        APPROVED: 0,
        Dispatched: 0,
        Confirmed: 0,
        Rejected: 0,
      };
    },
    [getAllStockRequests.rejected]: (state, { payload }) => {
      state.stockRequestsLoading = false;
      state.stockRequestsError =
        payload?.message || "Failed to fetch stock requests";
      state.stockRequestsData = [];
      state.stockRequestsTotalCount = 0;
      // Reset tab counts on error
      state.stockRequestsTabCounts = {
        PENDING: 0,
        APPROVED: 0,
        Dispatched: 0,
        Confirmed: 0,
        Rejected: 0,
      };
    },

    // Get Store Manager Orders
    [getStoreManagerOrders.pending]: (state) => {
      state.storeManagerOrdersLoading = true;
      state.storeManagerOrdersError = null;
    },
    [getStoreManagerOrders.fulfilled]: (state, { payload }) => {
      state.storeManagerOrdersLoading = false;
      state.storeManagerOrders = payload.data.orders.map((order) => ({
        ...order,
        order_date: new Date(order.order_date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }),
      }));
      state.storeManagerOrdersTotalCount = payload.data.pagination.total_orders;
      state.storeManagerOrdersCurrentPage =
        payload.data.pagination.current_page;
      state.storeManagerOrdersTotalPages = payload.data.pagination.total_pages;
      state.storeManagerOrdersPageSize = payload.data.pagination.page_size;
    },
    [getStoreManagerOrders.rejected]: (state, { payload }) => {
      state.storeManagerOrdersLoading = false;
      state.storeManagerOrdersError =
        payload?.message || "Failed to fetch store manager orders";
      state.storeManagerOrders = [];
      state.storeManagerOrdersTotalCount = 0;
      state.storeManagerOrdersCurrentPage = 1;
      state.storeManagerOrdersTotalPages = 1;
    },

    // Get stock request by ID
    [getStockRequestById.pending]: (state) => {
      state.currentStockRequestLoading = true;
      state.currentStockRequestError = null;
    },
    [getStockRequestById.fulfilled]: (state, { payload }) => {
      state.currentStockRequestLoading = false;
      state.currentStockRequest = payload.data;
      state.currentStockRequestError = null;
    },
    [getStockRequestById.rejected]: (state, { payload }) => {
      state.currentStockRequestLoading = false;
      state.currentStockRequestError =
        payload?.message || "Failed to fetch stock request";
      state.currentStockRequest = null;
    },

    // Get store analytics
    [getStoreAnalytics.pending]: (state) => {
      state.storeAnalyticsLoading = true;
      state.storeAnalyticsError = null;
    },
    [getStoreAnalytics.fulfilled]: (state, { payload }) => {
      state.storeAnalyticsLoading = false;
      state.storeAnalyticsData = payload.data || [];
      state.storeAnalyticsTotalCount = payload.total_items || 0;
      state.storeAnalyticsCurrentPage = payload.current_page || 1;
      state.storeAnalyticsTotalPages = payload.total_pages || 1;
    },
    [getStoreAnalytics.rejected]: (state, { payload }) => {
      state.storeAnalyticsLoading = false;
      state.storeAnalyticsError =
        payload?.message || "Failed to fetch store analytics";
      state.storeAnalyticsData = [];
      state.storeAnalyticsTotalCount = 0;
    },

    // Get restock requests
    [getRestockRequests.pending]: (state) => {
      state.restockRequestsLoading = true;
      state.restockRequestsError = null;
    },
    [getRestockRequests.fulfilled]: (state, { payload }) => {
      state.restockRequestsLoading = false;
      state.restockRequestsData = payload.data || [];
      state.restockRequestsTotalCount = payload.total_items || 0;
      state.restockRequestsCurrentPage = payload.current_page || 1;
      state.restockRequestsTotalPages = payload.total_pages || 1;
    },
    [getRestockRequests.rejected]: (state, { payload }) => {
      state.restockRequestsLoading = false;
      state.restockRequestsError =
        payload?.message || "Failed to fetch restock requests";
      state.restockRequestsData = [];
      state.restockRequestsTotalCount = 0;
    },

    // Stock Transfers
    [getStockTransfers.pending]: (state) => {
      state.stockTransfersLoading = true;
      state.stockTransfersError = null;
    },
    [getStockTransfers.fulfilled]: (state, { payload }) => {
      state.stockTransfersLoading = false;
      state.stockTransfersData = payload.data || [];
      state.stockTransfersTotalCount = payload.total_items || 0;
      state.stockTransfersCurrentPage = payload.current_page || 1;
      state.stockTransfersTotalPages = payload.total_pages || 1;
    },
    [getStockTransfers.rejected]: (state, { payload }) => {
      state.stockTransfersLoading = false;
      state.stockTransfersError =
        payload?.message || "Failed to fetch stock transfers";
      state.stockTransfersData = [];
      state.stockTransfersTotalCount = 0;
    },

    // Get stock record
    [getStockRecord.pending]: (state) => {
      state.stockRecordLoading = true;
      state.stockRecordError = null;
    },
    [getStockRecord.fulfilled]: (state, { payload }) => {
      state.stockRecordLoading = false;
      state.stockRecordData = payload.data || [];
      state.stockRecordTotalCount = payload.pagination.total_items || 0;
      state.stockRecordCurrentPage = payload.pagination.current_page || 1;
      state.stockRecordTotalPages = payload.pagination.total_pages || 1;
      state.stockRecordTotalQuantity = payload.total_available_quantity;
    },
    [getStockRecord.rejected]: (state, { payload }) => {
      state.stockRecordLoading = false;
      state.stockRecordError =
        payload?.message || "Failed to fetch stock record";
      state.stockRecordData = [];
      state.stockRecordTotalCount = 0;
    },

    // Get Notes
    [getStockNotes.pending]: (state) => {
      state.stockNotesLoading = true;
      state.stockNotesError = null;
    },
    [getStockNotes.fulfilled]: (state, { payload }) => {
      state.stockNotesLoading = false;
      state.stockNotes = payload.data || [];
    },
    [getStockNotes.rejected]: (state, { payload }) => {
      state.stockNotesLoading = false;
      state.stockNotesError = payload?.message || "Failed to fetch stock notes";
      state.stockNotes = [];
    },

    // Low Stock Items
    [getLowStockItems.pending]: (state) => {
      state.lowStockLoading = true;
      state.lowStockError = null;
    },
    [getLowStockItems.fulfilled]: (state, { payload }) => {
      state.lowStockLoading = false;
      state.lowStockData = payload.data || [];
      state.lowStockTotalCount = payload.total_items || 0;
      state.lowStockCurrentPage = payload.current_page || 1;
      state.lowStockTotalPages = payload.total_pages || 1;
    },
    [getLowStockItems.rejected]: (state, { payload }) => {
      state.lowStockLoading = false;
      state.lowStockError =
        payload?.message || "Failed to fetch low stock items";
      state.lowStockData = [];
      state.lowStockTotalCount = 0;
    },

    // Get stock request timeline
    [getStockRequestTimeline.pending]: (state) => {
      state.stockRequestTimelineLoading = true;
      state.stockRequestTimelineError = null;
    },
    [getStockRequestTimeline.fulfilled]: (state, { payload }) => {
      state.stockRequestTimelineLoading = false;
      console.log(payload.timeline);
      state.stockRequestTimeline = payload.timeline || [];
    },
    [getStockRequestTimeline.rejected]: (state, { payload }) => {
      state.stockRequestTimelineLoading = false;
      state.stockRequestTimelineError =
        payload?.message || "Failed to fetch stock request timeline";
      state.stockRequestTimeline = [];
    },

    // Approve/Reject Stock Request
    [approveStockRequest.pending]: (state) => {
      state.addStockLoading = true;
      state.addStockError = null;
    },
    [approveStockRequest.fulfilled]: (state, { payload }) => {
      state.addStockLoading = false;
    },
    [approveStockRequest.rejected]: (state, { payload }) => {
      state.addStockLoading = false;
      state.addStockError =
        payload?.message || "Failed to approve/reject stock request";
    },

    // Dispatch Stock request
    [dispatchStockRequest.pending]: (state) => {
      state.addStockLoading = true;
      state.addStockError = null;
    },
    [dispatchStockRequest.fulfilled]: (state, { payload }) => {
      state.addStockLoading = false;
    },
    [dispatchStockRequest.rejected]: (state, { payload }) => {
      state.addStockLoading = false;
      state.addStockError =
        payload?.message || "Failed to dispatch stock request";
    },

    [confirmStockRequest.pending]: (state) => {
      state.confirmStockLoading = true;
      state.confirmStockError = null;
    },
    [confirmStockRequest.fulfilled]: (state, { payload }) => {
      state.confirmStockLoading = false;
    },
    [confirmStockRequest.rejected]: (state, { payload }) => {
      state.confirmStockLoading = false;
      state.confirmStockError =
        payload?.message || "Failed to confirm stock request";
    },
  },
});

const storeManagementReducer = persistReducer(
  storeManagementPersistConfig,
  storeManagementSlice.reducer
);

export const { clearErrors } = storeManagementSlice.actions;

export default storeManagementReducer;
